import React from 'react';
import { tierName, tierColorVar } from '../constants/achievements';

/**
 * A single badge in the collection grid.
 *
 * Takes a grouped badge (see `buildBadges` in AchievementsPage) rather than a raw
 * achievement, so a tiered family renders as one medallion at its highest earned tier.
 *
 * Ring states: an incomplete badge with measurable progress shows a green arc toward
 * its next tier; an earned badge shows a solid ring — tier-coloured for a family,
 * brand green for a standalone, where a tier colour would imply a rank it has no;
 * a locked one is grey.
 */
const BadgeMedallion = ({ badge, onClick }) => {
    const { name, icon, earned, complete, tierIndex, progress, progressTarget } = badge;

    const showProgress = !complete && progress != null && progressTarget != null;
    const pct = showProgress
        ? Math.min(100, Math.round((progress / progressTarget) * 100))
        : 0;

    const earnedRing = badge.family ? tierColorVar(tierIndex) : 'var(--color-primary)';
    const ringStyle = showProgress
        ? { background: `conic-gradient(var(--color-primary) ${pct}%, var(--color-primary-track) 0)` }
        : { background: earned ? earnedRing : 'var(--color-tier-locked)' };

    const stateLabel = earned
        ? badge.family
            ? `${tierName(tierIndex)} tier, ${badge.earnedCount} of ${badge.totalCount} tiers`
            : 'earned'
        : showProgress
            ? `locked, ${progress} of ${progressTarget}`
            : 'locked';

    return (
        <button
            type="button"
            className={`ach-badge ${earned ? 'is-earned' : 'is-locked'}`}
            onClick={onClick}
            aria-label={`${name} — ${stateLabel}`}
        >
            <span className="ach-badge-ring" style={ringStyle}>
                <span className="ach-badge-face">
                    <span className="ach-badge-icon" aria-hidden="true">
                        {icon || '🏆'}
                    </span>
                </span>
            </span>

            <span className="ach-badge-name">{name}</span>

            {earned && badge.family && (
                <span className="ach-badge-tier" style={{ color: tierColorVar(tierIndex) }}>
                    {tierName(tierIndex)}
                </span>
            )}
            {!earned && showProgress && (
                <span className="ach-badge-tier">{progress}/{progressTarget}</span>
            )}
        </button>
    );
};

export default BadgeMedallion;
